import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import pptxgen from "pptxgenjs";
import ReactDOMServer from "react-dom/server";
import i18next from "i18next";

const renderSlideToContainer = async (
  slide: any,
  width: number,
  height: number,
  scale = 1,
) => {
  // Create a temporary container
  const container = document.createElement("div");
  container.style.width = `${width}px`;
  container.style.height = `${height}px`;
  container.style.position = "fixed";
  container.style.left = "0";
  container.style.top = "0";
  container.style.backgroundColor = "white";
  container.style.transform = `scale(${scale})`;
  container.style.transformOrigin = "top left";
  container.style.zIndex = "-9999";
  document.body.appendChild(container);

  // Render the slide content
  const slideElement = slide.schema.render(slide.content);
  const htmlString = ReactDOMServer.renderToString(slideElement);
  container.innerHTML = htmlString;

  // Wait for images and fonts to load
  await new Promise((resolve) => {
    const images = container.getElementsByTagName("img");
    let loadedImages = 0;

    if (images.length === 0) {
      resolve(true);
      return;
    }

    Array.from(images).forEach((img) => {
      if (img.complete) {
        loadedImages++;
        if (loadedImages === images.length) resolve(true);
      } else {
        img.onload = () => {
          loadedImages++;
          if (loadedImages === images.length) resolve(true);
        };
        img.onerror = () => {
          loadedImages++;
          if (loadedImages === images.length) resolve(true);
        };
      }
    });
  });

  // Additional wait for fonts and rendering
  await new Promise((resolve) => setTimeout(resolve, 500));

  return container;
};

export const exportToPDF = async (
  slideComponents: any[],
  unitTopic?: string,
  onProgress?: (progress: any) => void,
) => {
  const width = 1920;
  const height = 1080;
  const scale = 2;

  const pdf = new jsPDF({
    orientation: "landscape",
    unit: "px",
    format: [width, height],
  });

  try {
    for (let i = 0; i < slideComponents.length; i++) {
      onProgress?.({
        type: "pdf",
        current: i + 1,
        total: slideComponents.length,
        message: i18next.t("slides.export.processing", {
          current: i + 1,
          total: slideComponents.length,
        }),
      });

      const container = await renderSlideToContainer(
        slideComponents[i],
        width,
        height,
        scale,
      );

      try {
        const canvas = await html2canvas(container, {
          width: width * scale,
          height: height * scale,
          scale: 1,
          useCORS: true,
          logging: false,
          backgroundColor: "white",
          allowTaint: true,
          foreignObjectRendering: true,
          removeContainer: false,
          onclone: (clonedDoc) => {
            const clonedContainer = clonedDoc.querySelector("div");
            if (clonedContainer) {
              // Reset any transforms that might affect rendering
              clonedContainer.style.transform = "none";

              // Ensure all elements are visible
              const allElements = clonedContainer.getElementsByTagName("*");
              Array.from(allElements).forEach((el: any) => {
                if (el.style) {
                  el.style.transform = "none";
                  el.style.transition = "none";
                  if (getComputedStyle(el).position === "fixed") {
                    el.style.position = "absolute";
                  }
                }
              });
            }
          },
        });

        if (i > 0) {
          pdf.addPage();
        }

        const imgData = canvas.toDataURL("image/jpeg", 1.0);
        pdf.addImage(imgData, "JPEG", 0, 0, width, height);
      } finally {
        document.body.removeChild(container);
      }
    }

    onProgress?.({
      type: "pdf",
      current: slideComponents.length,
      total: slideComponents.length,
      message: i18next.t("slides.export.saving.pdf"),
    });

    pdf.save(`${unitTopic}.pdf`);

    onProgress?.({
      type: "pdf",
      current: slideComponents.length,
      total: slideComponents.length,
      message: i18next.t("slides.export.success.pdf"),
      done: true,
    });
  } catch (error) {
    console.error("Error generating PDF:", error);
    onProgress?.({
      type: "pdf",
      error: true,
      message: i18next.t("slides.export.error.pdf"),
    });
  }
};

export const exportToPPTX = async (
  slideComponents: any[],
  unitTopic?: string,
  onProgress?: (progress: any) => void,
) => {
  const width = 1920;
  const height = 1080;
  const scale = 2;

  const pptx = new pptxgen();
  pptx.layout = "LAYOUT_16x9";

  try {
    for (let i = 0; i < slideComponents.length; i++) {
      onProgress?.({
        type: "pptx",
        current: i + 1,
        total: slideComponents.length,
        message: i18next.t("slides.export.processing", {
          current: i + 1,
          total: slideComponents.length,
        }),
      });

      const container = await renderSlideToContainer(
        slideComponents[i],
        width,
        height,
        scale,
      );

      try {
        const canvas = await html2canvas(container, {
          width: width * scale,
          height: height * scale,
          scale: 1,
          useCORS: true,
          logging: false,
          backgroundColor: "white",
          allowTaint: true,
          foreignObjectRendering: true,
          removeContainer: false,
          onclone: (clonedDoc) => {
            const clonedContainer = clonedDoc.querySelector("div");
            if (clonedContainer) {
              clonedContainer.style.transform = "none";

              // Ensure all elements are visible
              const allElements = clonedContainer.getElementsByTagName("*");
              Array.from(allElements).forEach((el: any) => {
                if (el.style) {
                  el.style.transform = "none";
                  el.style.transition = "none";
                  if (getComputedStyle(el).position === "fixed") {
                    el.style.position = "absolute";
                  }
                }
              });
            }
          },
        });

        const imgData = canvas.toDataURL("image/jpeg", 1.0);

        const pptxSlide = pptx.addSlide();
        pptxSlide.addImage({
          data: imgData,
          x: 0,
          y: 0,
          w: "100%",
          h: "100%",
        });
      } finally {
        document.body.removeChild(container);
      }
    }

    onProgress?.({
      type: "pptx",
      current: slideComponents.length,
      total: slideComponents.length,
      message: i18next.t("slides.export.saving.pptx"),
    });

    await pptx.writeFile({ fileName: `${unitTopic}.pptx` });

    onProgress?.({
      type: "pptx",
      current: slideComponents.length,
      total: slideComponents.length,
      message: i18next.t("slides.export.success.pptx"),
      done: true,
    });
  } catch (error) {
    console.error("Error generating PPTX:", error);
    onProgress?.({
      type: "pptx",
      error: true,
      message: i18next.t("slides.export.error.pptx"),
    });
  }
};
